<template>
  <v-stepper
    non-linear
    v-model="step"
    vertical
    :style="$vuetify.theme.dark ? 'background: #1E1E1E;' : ''"
    class="mb-0"
  >
    <v-overlay
      v-model="overlay"
      :opacity="0.75"
      :absolute="!$vuetify.breakpoint.mobile"
    >
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular
          v-if="loading"
          color="primary"
          indeterminate
          size="26"
          width="4"
        ></v-progress-circular>

        <div v-else-if="addEventError">
          <v-icon color="red" size="32">mdi-alert-circle</v-icon>
        </div>

        <div v-else>
          <v-icon color="green" size="32">mdi-check</v-icon>
        </div>

        <div
          class="mt-4 mb-6 text-h6"
          :class="{
            'green--text': loading === false && addEventError === false,
            'red--text': loading === false && addEventError,
          }"
        >
          {{
            loading
              ? $t("createNewEvent.creatingEvent")
              : addEventError
              ? $t("createNewEvent.errors.creatingEventError")
              : $t("createNewEvent.eventCreated")
          }}
        </div>

        <v-btn
          @click="closeOverlay"
          color="primary"
          small
          v-if="loading === false"
        >
          {{ $t("toClose") }}
        </v-btn>
      </div>
    </v-overlay>

    <v-stepper-step :complete="step > 1" step="1">
      {{ $t("selectASport") }}
      <small v-if="form.sport && step > 1" class="caption">
        {{ form.sport.sport_name }}
      </small>
    </v-stepper-step>

    <v-stepper-content step="1" style="max-width: 400px">
      <v-select
        v-model="form.sport"
        :items="sports"
        :loading="fetchingSports"
        :disabled="fetchingSports"
        item-value="id"
        item-text="sport_name"
        class="mb-6"
        :label="$t('selectASport')"
        return-object
        dense
        filled
        hide-details
        solo-inverted
      />

      <v-btn
        small
        color="primary"
        @click="step = 2"
        :disabled="Object.keys(form.sport).length === 0"
      >
        {{ $t("continue") }}
      </v-btn>
      <v-btn small text class="ml-2" @click="closeDialog">
        {{ $t("toCancel") }}
      </v-btn>
    </v-stepper-content>

    <v-stepper-step :complete="step > 2" step="2">
      <div
        class="d-flex align-center"
        :style="
          $vuetify.breakpoint.width > 380 ? 'width: 330px;' : 'width: 100%;'
        "
      >
        <div style="width: 100%">
          {{ $t("createNewEvent.form.selectALeague") }}
          <br />
          <small v-if="form.league && step > 2" class="caption">
            {{ form.league.league_name }}
          </small>
        </div>
        <div
          class="text-subtitle-2"
          :class="$vuetify.breakpoint.xs ? 'ml-4' : ''"
        >
          <v-btn
            :style="$vuetify.theme.dark ? 'color: #C7C7C7' : ''"
            class="text-none"
            text
            small
            @click="createNewLeague"
            :disabled="step !== 2"
          >
            {{ $t("createNewEvent.leagueCreationTitle") }}
          </v-btn>
        </div>
      </div>
    </v-stepper-step>

    <v-stepper-content step="2" style="max-width: 400px">
      <v-select
        v-model="form.league"
        :items="filteredLeagues"
        :loading="fetchingLeagues"
        :disabled="fetchingLeagues"
        class="mb-6"
        :label="$t('createNewEvent.form.selectALeague')"
        item-text="league_name"
        item-value="id"
        return-object
        dense
        filled
        hide-details
        solo-inverted
      />

      <v-btn
        small
        color="primary"
        @click="step = 3"
        :disabled="fetchingLeagues || Object.keys(form.league).length === 0"
      >
        {{ $t("continue") }}
      </v-btn>
      <v-btn small text class="ml-2" @click="step = 1">
        {{ $t("toCancel") }}
      </v-btn>
    </v-stepper-content>

    <v-stepper-step :complete="step > 3" step="3" :rules="[dateError]">
      <div class="d-flex align-center" style="width: 100%">
        <div>
          {{ $t("createNewEvent.form.registerEvent") }}
          <br />
          <small v-if="form.home && form.away && step > 3" class="caption">
            {{ `${form.home} X ${form.away}` }}
            <br />
            {{
              getEventDate()
                .date.toLocaleString()
                .slice(0, -3)
            }}
          </small>
          <small v-if="errorMessages.day !== ''">
            {{ errorMessages.day }}
            <br />
          </small>
          <small v-if="errorMessages.hours !== ''">
            {{ errorMessages.hours }}
          </small>
          <br
            v-if="errorMessages.minutes !== '' && errorMessages.hours !== ''"
          />
          <small v-if="errorMessages.minutes !== ''">
            {{ errorMessages.minutes }}
          </small>
        </div>
      </div>
    </v-stepper-step>

    <v-stepper-content step="3" style="max-width: 800px">
      <div
        class="d-flex"
        :class="$vuetify.breakpoint.smAndDown ? 'flex-column' : ''"
        :style="$vuetify.breakpoint.smAndDown ? 'gap: 25px;' : 'gap: 70px;'"
      >
        <div class="d-flex flex-column">
          <v-text-field
            v-model="form.home"
            solo-inverted
            dense
            hide-details="auto"
            :rules="teamRules"
            :label="$t('createNewEvent.form.teamOne')"
            @keydown="teamNameKeydown($event)"
            maxlength="25"
            counter
          />

          <v-text-field
            v-model="form.away"
            solo-inverted
            dense
            hide-details="auto"
            :rules="teamRules"
            :label="$t('createNewEvent.form.teamTwo')"
            class="mt-2"
            @keydown="teamNameKeydown($event)"
            maxlength="25"
            counter
          />
        </div>

        <div class="d-flex flex-column">
          <div class="d-flex align-center mb-2">
            <v-icon>
              mdi-calendar
            </v-icon>
            <span class="ml-1">
              {{ $t("date") }}
            </span>
          </div>
          <v-text-field
            v-model="form.date.day"
            clearable
            solo-inverted
            dense
            hide-details
            placeholder="dd/mm/yyyy"
            v-mask="'##/##/####'"
            type="text"
          />
        </div>

        <div class="d-flex flex-column">
          <div class="d-flex align-center mb-2">
            <v-icon>
              mdi-clock
            </v-icon>
            <span class="ml-1">
              {{ $t("schedule") }}
            </span>
          </div>
          <div class="d-flex align-center">
            <v-text-field
              v-model="form.date.hours"
              :placeholder="$t('hourCapitalized')"
              solo-inverted
              dense
              hide-details="auto"
              type="number"
              step="1"
              min="0"
              max="24"
              onkeypress="return event.keyCode === 8 || event.charCode >= 48 && event.charCode <= 57"
            />
            <div class="mx-2">
              :
            </div>
            <v-text-field
              v-model="form.date.minutes"
              placeholder="Min"
              solo-inverted
              dense
              hide-details="auto"
              type="number"
              step="1"
              min="0"
              max="59"
              onkeypress="return event.keyCode === 8 || event.charCode >= 48 && event.charCode <= 57"
            />
          </div>
        </div>
      </div>

      <div class="mt-6">
        <v-btn
          small
          color="primary"
          type="submit"
          @click.prevent="step = 4"
          :disabled="disableEventRegisterNextStep"
        >
          {{ $t("continue") }}
        </v-btn>
        <v-btn small text class="ml-2" @click="step = 2">
          {{ $t("toCancel") }}
        </v-btn>
      </div>
    </v-stepper-content>

    <v-stepper-step :complete="step > 4" step="4">
      {{ $t("createNewEvent.form.registerOdds") }}
      <div v-if="step > 4 && form.validOdds" class="caption">
        <small v-for="odd in form.odds" :key="odd.id">
          {{ `${odd.description} - ${odd.price}` }}
          <br />
        </small>
      </div>
    </v-stepper-step>
    <v-stepper-content step="4" style="max-width: 800px">
      <v-form v-model="form.validOdds" @submit="step = 5">
        <div class="d-flex flex-column">
          <v-radio-group
            v-model="form.market"
            v-for="(market, i) in possibleMarkets"
            :key="market.value"
            class="d-flex flex-column pa-0 ma-0"
          >
            <v-radio
              hide-details
              :disabled="fetchingOdds"
              :value="market.value"
              :label="market.text"
              :class="{
                'ml-4': $vuetify.breakpoint.mobile,
                'ml-2': !$vuetify.breakpoint.mobile,
                'mb-4 mt-2': i === 0,
                'mb-4': i > 0,
              }"
            >
              <template v-slot:label>
                <div class="ml-1">
                  {{ $t("createNewEvent.marketInsert") }} {{ market.text }}
                </div>
                <div
                  v-if="form.odds.length > 0 && Object.keys(profit).length > 0"
                  class="d-flex align-center ml-6 disabled"
                  :class="profit.class"
                >
                  <div>
                    {{ profit.value }}
                  </div>
                  <div>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-on="on"
                          v-bind="attrs"
                          small
                          class="mb-1 ml-2 "
                          color="primary"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t("surebetFormula") }}</span>
                    </v-tooltip>
                  </div>
                </div>
              </template>
            </v-radio>

            <div v-if="form.market === market.value">
              <div v-if="fetchingOdds">
                <v-row align="center" justify="center">
                  <v-col class="d-flex align-center justify-center">
                    <v-progress-circular
                      indeterminate
                      color="info"
                    ></v-progress-circular>
                  </v-col>
                </v-row>
              </div>
              <div v-else-if="form.odds.length === 0">
                <v-alert class="mx-4" type="info" dense text border="left">
                  {{ $t("createNewEvent.errors.noOdds") }}
                </v-alert>
              </div>
              <div
                v-else
                class="d-flex flex-column mx-4"
                style="gap: 15px; max-width: 400px;"
              >
                <div
                  v-for="odd in form.odds"
                  :key="odd.bet_type_id"
                  style="flex-grow: 1;"
                >
                  <label>
                    {{ odd.description }}
                  </label>
                  <v-text-field
                    v-model="odd.price"
                    :rules="oddRules"
                    :placeholder="odd.description"
                    solo-inverted
                    dense
                    hide-details="auto"
                    type="number"
                    step="0.01"
                    min="1.01"
                    max="99"
                  />
                </div>
              </div>
            </div>
          </v-radio-group>
        </div>

        <div class="mt-0">
          <v-btn
            small
            color="primary"
            type="submit"
            @click.prevent="step = 5"
            :disabled="!form.validOdds || form.odds.length === 0"
          >
            {{ $t("continue") }}
          </v-btn>
          <v-btn small text class="ml-2" @click="step = 3">
            {{ $t("toCancel") }}
          </v-btn>
        </div>
      </v-form>
    </v-stepper-content>

    <div class="d-flex justify-end mr-4 mt-12" v-if="step === 5">
      <v-btn text class="mr-2" @click="step = 4">
        {{ $t("toCancel") }}
      </v-btn>

      <v-btn color="primary" @click="createEvent">
        {{ $t("toSave") }}
      </v-btn>
    </div>
  </v-stepper>
</template>

<script>
import { validate } from "@/util/validate";

export default {
  props: ["dialog", "sports", "fetchingSports"],

  data: () => ({
    step: 1,
    overlay: false,
    loading: false,
    fetchingLeagues: false,
    leagues: [],
    form: {
      sport: {},
      league: {},
      home: "",
      away: "",
      date: {
        day: "",
        hours: "",
        minutes: "",
      },
      market: null,
      odds: [],
      validOdds: false,
    },
    possibleMarkets: [
      {
        text: "1, 2",
        value: "12",
        outcome: "52",
      },
    ],
    oddRules: [validate.required, validate.minOddValue, validate.maxOddValue],
    teamRules: [validate.required, validate.atLeastTwoChars],
    errorMessages: {
      day: "",
      hours: "",
      minutes: "",
    },
    fetchingOdds: false,
    addEventError: false,
    eventAdded: false,
    profit: {},
  }),

  created() {
    this.fetchLeagues();
  },

  computed: {
    filteredLeagues() {
      if (this.form.sport.id) {
        return this.leagues.filter(
          (league) => league.sport_id == this.form.sport.id
        );
      }

      return this.leagues;
    },
    disableEventRegisterNextStep() {
      if (!this.form.home || !this.form.away) {
        return true;
      }

      if (
        !this.form.date.day ||
        !this.form.date.hours ||
        !this.form.date.minutes
      ) {
        return true;
      }

      if (
        this.errorMessages.minutes !== "" ||
        this.errorMessages.hours !== "" ||
        this.errorMessages.day !== ""
      ) {
        return true;
      }

      return false;
    },
  },

  watch: {
    "form.home"(val) {
      this.form.home = val.replace(/[^ a-zA-Zà-üÀ-Ü\d]/, "");
    },
    "form.away"(val) {
      this.form.away = val.replace(/[^ a-zA-Zà-üÀ-Ü\d]/, "");
    },
    dialog(val) {
      if (val) {
        this.eventAdded = false;
        this.fetchLeagues();
      } else {
        if (this.eventAdded) {
          this.$emit("fetchCreatedEvents");
        }

        this.closeOverlay();
      }
    },
    "form.market"(val) {
      if (this.fetchingOdds || !this.dialog) {
        return;
      }

      const body = {
        sport_id: this.form.sport.id,
        outcome_id: this.possibleMarkets.find((market) => market.value === val)
          .outcome,
      };

      this.fetchingOdds = true;
      this.form.odds = [];

      this.$axios
        .post(
          `/${this.$prefix()}/manualEvents/odds/getBetTypeIds`,
          this.$formdata(body)
        )
        .then((res) => {
          this.form.odds = res.data.dados.map((o) => ({
            ...o,
            description:
              o.description.charAt(0).toUpperCase() + o.description.slice(1),
            price: null,
          }));
        })
        .finally(() => {
          this.fetchingOdds = false;
        });
    },
    step(val) {
      if (val != 3) {
        this.errorMessages.day = "";
        this.errorMessages.hours = "";
        this.errorMessages.minutes = "";
      }
    },
    "form.odds": {
      handler(odds) {
        this.profit = this.getPotentialProfitPercentages({
          odds,
          field: "price",
        });
      },
      deep: true,
    },

    "form.sport"() {
      this.form.market = null;
      this.form.odds = [];
      this.form.league = {};
    },
  },

  methods: {
    teamNameKeydown(e) {
      if (/[^ a-zA-Zà-üÀ-Ü\d]/.test(e.key)) {
        e.preventDefault();
      }
    },

    updateLeagues(newLeague) {
      this.leagues = [...this.leagues, newLeague];
    },

    dateError() {
      if (this.step != 3) {
        return true;
      }

      const min = parseFloat(this.form.date.minutes);
      const hour = parseFloat(this.form.date.hours);

      if (0 > min) {
        this.errorMessages.minutes = this.errorMessages.minutes = this.$t(
          "createNewEvent.errors.negativeMinute"
        );
      } else if (min > 59) {
        this.errorMessages.minutes = this.$t(
          "createNewEvent.errors.wrongMinute"
        );
      } else {
        this.errorMessages.minutes = "";
      }

      if (0 > hour) {
        this.errorMessages.hours = this.$t(
          "createNewEvent.errors.negativeHour"
        );
      } else if (hour > 24) {
        this.errorMessages.hours = this.$t("createNewEvent.errors.wrongHour");
      } else {
        this.errorMessages.hours = "";
      }

      function isValidDate(d) {
        return d instanceof Date && !isNaN(d);
      }

      if (!this.form.date.day) {
        this.errorMessages.day = this.$t("createNewEvent.errors.selectDay");
      } else if (this.form.date.day) {
        let eventDate = this.getEventDateByDay();
        const now = new Date();

        if (isValidDate(eventDate) === false) {
          this.errorMessages.day =
            this.form.date.hours && this.form.date.minutes
              ? this.$t("createNewEvent.errors.invalidDay")
              : this.$t("createNewEvent.errors.selectHourAndMinute");
        } else if (this.form.date.hours && this.form.date.minutes) {
          const { date } = this.getEventDate();

          if (now > date) {
            this.errorMessages.day = this.$t(
              "createNewEvent.errors.eventDateAlreadyHappened"
            );
          } else {
            const now = new Date();
            const eventDate = this.getEventDateByDay();
            const timeDiff = eventDate.getTime() - now.getTime();
            const daysDiff = timeDiff / (1000 * 3600 * 24);

            this.errorMessages.day =
              daysDiff > 7
                ? this.$t("createNewEvent.errors.moreThanSevenDays")
                : "";
          }
        } else if (now.getTime() > eventDate.getTime()) {
          this.errorMessages.day =
            this.form.date.hours && this.form.date.minutes
              ? this.$t("createNewEvent.errors.eventDateAlreadyHappened")
              : this.$t("createNewEvent.errors.selectHourAndMinute");
        }
      } else {
        this.errorMessages.day = "";
      }

      return !(
        this.errorMessages.hours !== "" ||
        this.errorMessages.minutes !== "" ||
        this.errorMessages.day != ""
      );
    },
    setLeague({ league, sport }) {
      this.form.sport = sport;
      this.leagues = [...this.leagues, league];
      this.form.league = league;
    },
    createNewLeague() {
      this.$emit("createNewLeague", { sport: this.form.sport });
    },
    getEventDateByDay() {
      const splittedEventDate = this.form.date.day.split("/");
      const eventDate = `${splittedEventDate[2]}-${splittedEventDate[1]}-${splittedEventDate[0]}`;
      const hoursMinutesSeconds = "00:00:00";
      const date = new Date(
        `${eventDate} ${hoursMinutesSeconds}`.replace(" ", "T")
      );
      return date;
    },
    getEventDate() {
      const splittedEventDate = this.form.date.day.split("/");
      const eventDate = `${splittedEventDate[2]}-${splittedEventDate[1]}-${splittedEventDate[0]}`;
      if (this.form.date.hours == "24") {
        this.form.date.hours = 0;
      }
      const formattedHour = this.form.date.hours
        ? ("0" + this.form.date.hours).slice(-2)
        : "00";
      const formattedMinute = this.form.date.minutes
        ? ("0" + this.form.date.minutes).slice(-2)
        : "00";
      const hourAndMinute = `${formattedHour}:${formattedMinute}:00`;
      const str = `${eventDate} ${hourAndMinute}`;
      const date = new Date(`${eventDate} ${hourAndMinute}`.replace(" ", "T"));

      return { date, str };
    },
    createEvent() {
      const { str } = this.getEventDate();

      const formdata = new FormData();
      formdata.append("scheduled", str);
      formdata.append("home_team", this.form.home);
      formdata.append("away_team", this.form.away);
      formdata.append("sport_id", this.form.sport.id);
      formdata.append("league_id", this.form.league.id);

      const { outcome } = this.possibleMarkets.find(
        (market) => market.value === this.form.market
      );

      formdata.append("outcome_id", outcome);
      formdata.append("odds", JSON.stringify(this.form.odds));

      this.overlay = true;
      this.loading = true;

      this.$axios
        .post(`/${this.$prefix()}/manualEvents/events/addEvent`, formdata)
        .then(() => {
          this.addEventError = false;
          this.eventAdded = true;
        })
        .catch(() => {
          this.addEventError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchLeagues() {
      if (this.fetchingLeagues) {
        return;
      }

      this.fetchingLeagues = true;

      this.$axios
        .get(`/${this.$prefix()}/manualEvents/leagues/listLeagues`)
        .then((res) => {
          this.leagues = res.data.dados;
        })
        .finally(() => {
          this.fetchingLeagues = false;
        });
    },

    closeDialog() {
      this.$emit("closeDialog");
    },

    closeOverlay() {
      this.overlay = false;
      this.addEventError = false;
      this.clearStepper();
    },

    clearStepper() {
      this.form.sport = {};
      this.form.league = {};
      this.form.home = "";
      this.form.away = "";
      this.form.date.day = "";
      this.form.date.minutes = "";
      this.form.date.hours = "";
      this.form.market = null;
      this.form.odds = [];
      this.profit = {};
      this.step = 1;
    },

    getInverseOddsSum({ odds, field }) {
      let inverseOddsSum = 0;

      for (let i = 0; i < odds.length; i++) {
        inverseOddsSum += 1 / Number(odds[i][field]);
      }

      return inverseOddsSum;
    },

    getPotentialProfitPercentages({ odds, field }) {
      const inverseOddsSum = this.getInverseOddsSum({ odds, field });

      const profit = ((inverseOddsSum - 1) / inverseOddsSum) * 100;

      if (isNaN(profit)) {
        return {};
      }

      const value = `${
        profit > 0
          ? `+${this.decimalAdjustment(profit)}`
          : this.decimalAdjustment(profit)
      }%`;

      return {
        value,
        class:
          profit > 0 ? "green--text" : profit < 0 ? "red--text" : "grey--text",
      };
    },

    decimalAdjustment(num) {
      return (-(Math.round(-num + "e+2") + "e-2")).toFixed(2);
    },
  },
};
</script>
<style>
.v-stepper {
  box-shadow: none;
}
</style>
